import React from 'react';
import {
  BarChart, Clapperboard, CreditCard, FileText, Film, Gift, Home, Image as ImageIcon, LayoutGrid, Play, ShoppingCart, Sparkles, Tag, UserCog, Users, Video, Workflow,
} from 'lucide-react';
import { MARKETPLACE_ENABLED } from './featureFlags';

export type AppModule = {
  id: string;
  title: string;
  icon: React.ReactNode;
  allowedRoles: string[];
  /** Only for organizations on the Individual plan. */
  isAutonomousOnly?: boolean;
  /** Studio allowlist (`platform_creators`), not an org role. */
  requiresCreator?: boolean;
};

const ALL = ['admin', 'fin_ops', 'employee'];

/** Creation tools: what the floating sidebar shows. */
export const TOOL_MODULES: AppModule[] = [
  { id: '/explorar', title: 'Explorar', icon: <Play size={18} />, allowedRoles: ALL },
  { id: '/estudio', title: 'Estúdio', icon: <Clapperboard size={18} />, allowedRoles: ALL, requiresCreator: true },
  { id: '/tela-em-branco', title: 'Tela em branco', icon: <Workflow size={18} />, allowedRoles: ALL },
  { id: '/painel-criacao', title: 'Painel de Criação', icon: <Video size={18} />, allowedRoles: ALL },
  { id: '/editor', title: 'Editor de Vídeo', icon: <Film size={18} />, allowedRoles: ALL },
  { id: '/mobiliacao', title: 'Mobiliação IA', icon: <Sparkles size={18} />, allowedRoles: ALL },
  { id: '/galeria', title: 'Galeria de Mídia', icon: <ImageIcon size={18} />, allowedRoles: ALL },
  { id: '/kanban', title: 'Kanban', icon: <LayoutGrid size={18} />, allowedRoles: ALL },
  { id: '/marketplace', title: 'Marketplace', icon: <ShoppingCart size={18} />, allowedRoles: ALL },
];

/** Account, billing and management: what the profile menu shows. */
export const ACCOUNT_MODULES: AppModule[] = [
  { id: '/conta', title: 'Configurações', icon: <UserCog size={18} />, allowedRoles: ALL },
  { id: '/app', title: 'Dashboard', icon: <Home size={18} />, allowedRoles: ALL },
  { id: '/gerenciamento/usuarios', title: 'Usuários & FinOps', icon: <Users size={18} />, allowedRoles: ['admin', 'fin_ops'] },
  { id: '/financeiro', title: 'Financeiro', icon: <BarChart size={18} />, allowedRoles: ['admin', 'fin_ops'] },
  { id: '/pagamento', title: 'Pagamentos', icon: <CreditCard size={18} />, allowedRoles: ['admin', 'fin_ops'] },
  { id: '/solicitacoes', title: 'Solicitações', icon: <FileText size={18} />, allowedRoles: ALL },
  { id: '/tags', title: 'Tags de Cena', icon: <Tag size={18} />, allowedRoles: ['admin'] },
  { id: '/indicacoes', title: 'Indicações', icon: <Gift size={18} />, allowedRoles: ['admin'], isAutonomousOnly: true },
];

export const ALL_MODULES: AppModule[] = [...TOOL_MODULES, ...ACCOUNT_MODULES];

export function modulesForRole(
  list: AppModule[],
  role: string | null | undefined,
  isAutonomous: boolean,
  isCreator = false,
): AppModule[] {
  if (!role) return [];
  return list.filter((m) => {
    if (!m.allowedRoles.includes(role)) return false;
    if (m.requiresCreator && !isCreator) return false;
    if (m.isAutonomousOnly && !isAutonomous) return false;
    if (!MARKETPLACE_ENABLED && m.id === '/marketplace') return false;
    return true;
  });
}

/** Photo from the auth provider (Google) or the one saved on the account page. */
export function avatarUrlOf(meta: Record<string, unknown> | undefined): string {
  const url = meta?.avatar_url || meta?.picture;
  return typeof url === 'string' ? url : '';
}

export function displayNameOf(meta: Record<string, unknown> | undefined, email?: string | null): string {
  const name = meta?.full_name || meta?.name;
  if (typeof name === 'string' && name.trim()) return name.trim();
  return (email || '').split('@')[0] || 'Usuário';
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
